/* Product documents — the reader.

   docs/components/product-documents.md § The reader carries the design and every
   measured trap; read it before editing. The contract that shapes this file:
   A DOCUMENT IS NEVER A DEAD END. Before the reader opens, open() returns false
   and the click follows the row's real href into a new tab; after it opens, the
   sheet says so and the Download beside the message still carries the file. */
(function () {
  'use strict';

  /* Per-canvas ceiling: 16 MP is ~4000x4000, inside iOS Safari's per-canvas
     limit; an A3 page at 3x would want 40 MP and kill a phone tab (doc § The reader). */
  var MAX_CANVAS_PIXELS = 16e6;
  /* PDFs and images open in the reader; anything else is left to the browser
     (doc § What a document can be). */
  var PDF = /\.pdf(\?|$)/i;
  var IMAGE = /\.(jpe?g|png|gif|webp|avif|svg)(\?|$)/i;

  /* Generous but finite: a stalled socket used to show "Loading the document"
     for ever (doc § Nothing waits for ever). */
  var LOAD_DEADLINE = 30000;

  /* Zoom is a FACTOR, not an index into stops — a pinch arrives as an arbitrary
     ratio. One number, four drivers: the buttons (multiply by ZOOM_STEP), the
     pinch, the double-tap and the trackpad (doc § The reader). */
  var MIN_ZOOM = 1;      /* fit width — the floor, and the reader says so */
  /* A runaway guard, not a policy (owner, 2026-09-05: no ceiling). Keeps a factor
     finite for the scroll-anchoring arithmetic; sharpness is capped by
     MAX_CANVAS_PIXELS, not by this. */
  var MAX_ZOOM = 40;
  var ZOOM_STEP = 1.5;   /* one button press */
  var TAP_ZOOM = 2;      /* what a double-tap goes to, and back from */

  var pdfjs = null;
  var pdfjsPromise = null;
  var worker = null;

  /* ONE worker for the page: a loading task destroys only a worker it created,
     so handing it this one stops every open re-parsing 1.32 MB of worker JS
     (doc § The reader, "One worker for the page"). */
  function sharedWorker(lib) {
    if (worker && !worker.destroyed) return worker;
    worker = new lib.PDFWorker();
    return worker;
  }

  /* Resolves with the pdf.js module, or rejects. The promise itself is the cache,
     in flight included, so two fast clicks do not fetch 1.8 MB twice. The import
     goes through the section's one-line inline-module bridge (platform fact #33). */
  function loadPdfJs(libUrl, workerUrl) {
    if (pdfjs) return Promise.resolve(pdfjs);
    if (pdfjsPromise) return pdfjsPromise;
    var bridge = window.JLL && window.JLL.import;
    if (!bridge || !libUrl || !workerUrl) return Promise.reject(new Error('no pdf.js bridge'));

    /* Resolve against the document first: a dynamic import treats a BARE string
       as a package specifier rather than a path, so a relative url throws. */
    pdfjsPromise = bridge(new URL(libUrl, document.baseURI).href).then(function (mod) {
      if (!mod || !mod.getDocument) throw new Error('pdf.js loaded but is not usable');
      /* Theme assets are same-origin (verified 2026-09-04), so the worker can be
         named directly; cross-origin it would be a SecurityError needing a blob shim. */
      mod.GlobalWorkerOptions.workerSrc = new URL(workerUrl, document.baseURI).href;
      pdfjs = mod;
      return mod;
    });
    pdfjsPromise['catch'](function () { pdfjsPromise = null; });
    return pdfjsPromise;
  }

  /* ---------------------------------------------------------------- element */

  function ProductDocuments() {
    return Reflect.construct(HTMLElement, [], ProductDocuments);
  }
  ProductDocuments.prototype = Object.create(HTMLElement.prototype);
  ProductDocuments.prototype.constructor = ProductDocuments;
  Object.setPrototypeOf(ProductDocuments, HTMLElement);

  ProductDocuments.prototype.connectedCallback = function () {
    if (this.wired) return;
    this.wired = true;
    this.addEventListener('click', this.onClick.bind(this));

    /* The markup announces "Opens in a new window." because without this script
       that is what a row does. Retracted per ROW — willRead() is a per-row answer,
       and a .docx beside two PDFs still opens in a new tab (doc § What a document can be). */
    var links = this.querySelectorAll('.pdocs__link');
    for (var i = 0; i < links.length; i++) {
      if (!this.willRead(links[i])) continue;
      var says = links[i].querySelector('.visually-hidden');
      if (says) says.remove();
    }
  };

  /* THE one test that decides whether a click is intercepted, asked per ROW by
     both callers, so a row's announcement and its behaviour can never disagree. */
  ProductDocuments.prototype.willRead = function (link) {
    var url = link && link.getAttribute('href');
    if (!url) return false;
    var modal = this.modal();
    if (!modal || typeof modal.show !== 'function' || !parts(modal)) return false;
    /* An image needs only the sheet; a PDF also needs the section's bridge, whose
       absence IS the capability test (platform fact #33). */
    if (IMAGE.test(url)) return true;
    return !!(PDF.test(url) && window.JLL && window.JLL.import);
  };

  /* Dawn's modal-dialog moves itself to <body> and never back; a theme-editor
     re-render would leave a stale twin with the same id (doc § The reader).
     Same cleanup as assets/addon-selector.js. */
  ProductDocuments.prototype.disconnectedCallback = function () {
    var modal = this.modal();
    if (!modal || modal.parentElement !== document.body) return;
    if (modal.reader) modal.reader.teardown();
    modal.remove();
  };

  ProductDocuments.prototype.onClick = function (event) {
    var link = event.target.closest ? event.target.closest('.pdocs__link') : null;
    if (!link || !this.contains(link)) return;
    /* Let the customer's own intent win: a modified click means "new tab" and
       any viewer we open instead would be us overruling them. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    if (this.open(link)) event.preventDefault();
  };

  /* Dawn's modal-dialog moves itself to <body> on connect, so it is found by id
     and never by querySelector on this root. */
  ProductDocuments.prototype.modal = function () {
    return document.getElementById('DocumentViewer-' + this.dataset.sectionId);
  };

  ProductDocuments.prototype.open = function (link) {
    if (!this.willRead(link)) return false;
    var modal = this.modal();

    var url = link.getAttribute('href');

    /* One reader per modal, kept on the element: reopening a second document
       reuses it, so pdf.js is initialised once per page rather than per click. */
    var reader = modal.reader || (modal.reader = new Reader(modal));
    var title = link.querySelector('.pdocs__title');
    modal.show(link);
    reader.open(url, title ? title.textContent.trim() : '');
    return true;
  };

  /* ----------------------------------------------------------------- reader */

  /* The sheet's eleven parts, asserted ONCE in willRead(): a missing one is a typo
     in our own Liquid, and the reader declines rather than "working" with a dead
     Download button (doc § The reader). */
  var PARTS = ['scroll', 'title', 'count', 'prev', 'next', 'zoom-in', 'zoom-out', 'download', 'status', 'nav', 'scale'];

  function parts(modal) {
    var els = {};
    for (var i = 0; i < PARTS.length; i++) {
      var el = modal.querySelector('[data-pdocs-' + PARTS[i] + ']');
      if (!el) return null;
      els[PARTS[i].replace(/-(\w)/, function (m, c) { return c.toUpperCase(); })] = el;
    }
    return els;
  }

  function Reader(modal) {
    this.modal = modal;
    this.els = parts(modal);
    this.copy = {
      loading: modal.getAttribute('data-copy-loading') || 'Loading',
      failed: modal.getAttribute('data-copy-failed') || 'Could not display this document.'
    };
    this.zoom = MIN_ZOOM;
    this.pages = [];
    this.width = 0;
    this.doc = null;
    this.token = 0;
    this.bind();
  }

  Reader.prototype.bind = function () {
    var self = this;
    this.els.prev.addEventListener('click', function () { self.step(-1); });
    this.els.next.addEventListener('click', function () { self.step(1); });
    this.els.zoomIn.addEventListener('click', function () { self.zoomTo(self.zoom * ZOOM_STEP); self.repaint(); });
    this.els.zoomOut.addEventListener('click', function () { self.zoomTo(self.zoom / ZOOM_STEP); self.repaint(); });
    this.els.download.addEventListener('click', this.save.bind(this));

    /* Arrow keys page through, but only when focus is not in a control — the
       toolbar buttons must stay operable by keyboard. */
    this.modal.addEventListener('keydown', function (e) {
      if (e.target.closest && e.target.closest('button, a')) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') { self.step(1); e.preventDefault(); }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') { self.step(-1); e.preventDefault(); }
    });

    /* Click off the sheet to dismiss. Not Dawn's `media-modal` (it also dismisses
       on clicks INSIDE, on Zoom), and Dawn's backdrop branch never fires here
       (doc § The reader, the traps). */
    this.modal.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('.pdocs-viewer__sheet')) return;
      self.modal.hide();
    });

    /* Watch the attribute, not Dawn's `modalClosed`: hide() dispatches that BEFORE
       removing `open`, so an event listener skipped the cleanup (doc § Lifecycle). */
    new MutationObserver(function () {
      if (!self.modal.hasAttribute('open')) self.teardown();
    }).observe(this.modal, { attributes: true, attributeFilter: ['open'] });

    /* The observer fires only when a page crosses its band; the counter has to
       follow the finger. rAF-throttled so a flick costs one read per frame. */
    var ticking = false;
    this.els.scroll.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; self.updateCurrent(); });
    }, { passive: true });

    /* No feature tests on the observers: Dawn's global.js constructs all three
       unguarded on every page, so a browser without them is broken long before here. */
    var pending;
    this.resizer = new ResizeObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () { self.reflow(); }, 120);
    });

    this.bindGestures();
  };

  /* ------------------------------------------------------------- gestures */

  /* Pinch, double-tap and trackpad, all driving the one zoom number; the other
     half is `touch-action` in the CSS. A gesture frame only lays out and the
     re-render happens once when the fingers lift — disconnecting the observer is
     what enforces that, since the observer IS what paints (doc § Gestures). */
  Reader.prototype.bindGestures = function () {
    var self = this;
    var scroll = this.els.scroll;
    /* THE PINCH LISTENS ON THE WHOLE MODAL, the taps on the scroller: a zoom-in
       pinch starts with the fingers wide apart, off the scroller (owner report,
       2026-09-05; doc § The reader). The modal is also the element `touch-action`
       is on, so the refused region and the listened region are one region. */
    var surface = this.modal;
    var pinch = null;
    var down = null;                    /* where the current single touch began */
    var tap = { at: 0, x: 0, y: 0 };    /* where the last real TAP ended */
    var sharpen;
    var safari = false;                 /* Safari's own gesture events are driving */

    var spread = function (t) { return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY); };
    var middle = function (t) { return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 }; };

    /* Attached only while two fingers are down: a permanent non-passive touchmove
       would take one-finger scrolling off the compositor thread. */
    var move = function (e) {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      var m = middle(e.touches);
      self.zoomTo(pinch.zoom * (spread(e.touches) / pinch.spread), m.x, m.y);
    };

    var lift = function () {
      if (!pinch) return;
      var moved = self.zoom !== pinch.zoom;
      pinch = null;
      surface.removeEventListener('touchmove', move);
      /* Only if the fingers actually changed the zoom: a mis-grab used to restart
         every visible page's render for nothing. */
      if (moved) self.repaint(); else self.watch();
    };

    surface.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2 && !safari) {
        pinch = { spread: spread(e.touches), zoom: self.zoom };
        down = null;
        if (self.observer) self.observer.disconnect();
        surface.addEventListener('touchmove', move, { passive: false });
        return;
      }
      if (e.touches.length !== 1) { down = null; return; }

      /* Double-tap: to TAP_ZOOM on the tapped point, back to fit width from above
         it. Compared against the last real TAP (recorded in touchend), NEVER the
         last touch-down; and only on the document, not the chrome (doc § Gestures). */
      if (!e.target.closest || !e.target.closest('.pdocs-viewer__scroll')) { down = null; return; }
      var t = e.touches[0];
      var now = Date.now();
      down = { at: now, x: t.clientX, y: t.clientY };
      if (now - tap.at < 300 && Math.abs(t.clientX - tap.x) < 30 && Math.abs(t.clientY - tap.y) < 30) {
        tap.at = 0;
        down = null;
        self.zoomTo(self.zoom > MIN_ZOOM ? MIN_ZOOM : TAP_ZOOM, t.clientX, t.clientY);
        self.repaint();
      }
    }, { passive: true });

    /* A tap is one finger off quickly and close to where it landed; a swipe never
       arms the double-tap. */
    surface.addEventListener('touchend', function (e) {
      /* Three fingers to two mid-pinch: re-baseline, do not end — touchend is the
         only event for a REMOVED finger (doc § Gestures). */
      if (pinch && e.touches.length === 2) {
        pinch = { spread: spread(e.touches), zoom: self.zoom };
        return;
      }
      lift();
      if (!down || e.touches.length) { down = null; return; }
      var t = e.changedTouches[0];
      var quick = Date.now() - down.at < 250;
      var still = Math.abs(t.clientX - down.x) < 10 && Math.abs(t.clientY - down.y) < 10;
      tap = quick && still ? down : { at: 0, x: 0, y: 0 };
      down = null;
    });

    surface.addEventListener('touchcancel', function () { lift(); down = null; });

    /* A trackpad pinch is a wheel event carrying ctrlKey: exponential, and
       debounced rather than lifted because a wheel has no end. */
    surface.addEventListener('wheel', function (e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (self.observer) self.observer.disconnect();
      self.zoomTo(self.zoom * Math.exp(-e.deltaY / 180), e.clientX, e.clientY);
      clearTimeout(sharpen);
      sharpen = setTimeout(function () { self.repaint(); }, 150);
    }, { passive: false });

    /* SAFARI ignores `touch-action` for pinch-zoom; preventDefault() on its own
       gesture* events is the only refusal, and they carry a cumulative `scale`
       (platform fact #35). Chrome and Firefox never fire them; the flag keeps the
       touch path from fighting this one for the same fingers. */
    var pinchBase = MIN_ZOOM;
    surface.addEventListener('gesturestart', function (e) {
      e.preventDefault();
      safari = true;
      pinch = null;
      pinchBase = self.zoom;
      if (self.observer) self.observer.disconnect();
    });
    surface.addEventListener('gesturechange', function (e) {
      e.preventDefault();
      self.zoomTo(pinchBase * e.scale, e.clientX, e.clientY);
    });
    surface.addEventListener('gestureend', function (e) {
      e.preventDefault();
      safari = false;
      if (self.zoom !== pinchBase) self.repaint(); else self.watch();
    });
  };

  /* --------------------------------------------------------------- opening */

  Reader.prototype.open = function (url, title) {
    var self = this;

    /* Tear down BEFORE claiming a token: teardown() bumps the same counter, so a
       token taken first is stale by the time the load resolves (doc § Lifecycle). */
    this.teardown();
    var token = ++this.token;

    this.url = url;
    this.mime = PDF.test(url) ? 'application/pdf' : '';
    this.title = title;
    this.els.title.textContent = title;
    /* THE ANCHOR IS THE DOWNLOAD, set before anything can fail; `download` is
       honoured because these files are same-origin (doc § The reader). save()
       takes the click only when it can beat this. */
    this.els.download.setAttribute('href', url);
    this.els.download.setAttribute('download', this.filename());
    this.setStatus(this.copy.loading);
    this.zoom = MIN_ZOOM;

    /* An image is one page and needs no library; the sheet, zoom, pinch and
       Download around it are shared. */
    if (IMAGE.test(url)) { this.showImage(url, token); return; }

    loadPdfJs(this.modal.getAttribute('data-pdfjs'), this.modal.getAttribute('data-pdfjs-worker'))
      .then(function (lib) {
        if (token !== self.token) return null;
        /* Keep the loading TASK, not just its promise: pdf.js 6 dropped
           PDFDocumentProxy.destroy(), and the task is the only thing that frees a
           document's worker-side memory (doc § Lifecycle). */
        self.loading = lib.getDocument({
          url: url,
          worker: sharedWorker(lib),
          /* The assets DIRECTORY — Shopify's folder is flat and pdf.js appends the
             name. One cMap is vendored; a missing one degrades glyphs, nothing
             breaks (doc § The cMap). */
          cMapUrl: self.modal.getAttribute('data-assets') || undefined,
          cMapPacked: true
        });

        /* Say how it is going, and give up if it never does — a stalled socket,
           unlike a rejecting fetch, never fails on its own (doc § Nothing waits
           for ever). */
        self.loading.onProgress = function (p) {
          /* done() nulls `self.loading`, so this cannot speak after the document
             is on screen or after the deadline: pdf.js keeps streaming AFTER its
             promise resolves. */
          if (token !== self.token || !p || !p.total) return;
          self.setStatus(self.copy.loading + ' ' + Math.round(100 * p.loaded / p.total) + '%');
        };

        return Promise.race([
          self.loading.promise,
          new Promise(function (_, reject) {
            /* On the reader so teardown() can clear it; destroy() also stops the
               TRANSFER, not just the wait (doc § Nothing waits for ever). */
            self.deadline = setTimeout(function () {
              if (self.loading) { try { self.loading.destroy(); } catch (e) { /* already gone */ } }
              reject(new Error('timed out'));
            }, LOAD_DEADLINE);
          }),
        ]);
      })
      .then(function (doc) {
        self.done();
        if (!doc || token !== self.token) return;
        self.doc = doc;
        self.layout();
      })
      ['catch'](function (err) {
        if (token !== self.token) return;
        /* Say why, once, on the console: the customer gets the plain message and a
           working Download; whoever debugs it later gets the actual reason. */
        if (window.console && console.warn) console.warn('product-documents: cannot open', url, err);
        self.fail();
      });
  };

  /* One page, no library. The box carries the image's aspect ratio like the PDF
     boxes do, and the <img> shares `.pdocs-page__canvas` (`width: 100%; height:
     auto`), which is what makes zoom and pinch work here for free. */
  Reader.prototype.showImage = function (url, token) {
    var self = this;
    var img = new Image();
    img.className = 'pdocs-page__canvas';
    /* Named, not decorative: this <img> is the whole content of the reader, and
       `alt=""` would tell a screen reader there is nothing here. */
    img.alt = self.title || '';
    /* The same ceiling the PDF branch gets: `onerror` never fires for a socket
       that is accepted and then answers nothing (doc § Nothing waits for ever). */
    var timer = setTimeout(function () { if (token === self.token) self.fail(); }, LOAD_DEADLINE);
    img.onload = function () {
      clearTimeout(timer);
      if (token !== self.token) return;
      var box = document.createElement('div');
      box.className = 'pdocs-page';
      box.style.aspectRatio = img.naturalWidth + ' / ' + img.naturalHeight;
      box.dataset.page = '1';
      box.appendChild(img);
      self.els.scroll.replaceChildren(box);
      /* No `page`, so paint() and release() leave it alone — the browser is
         already the renderer, and it rescales without our help. */
      self.pages = [{ page: null, box: box, task: null, scale: 0 }];
      self.setStatus('');
      self.reflow();
      self.updateChrome();
      self.resizer.observe(self.els.scroll);
    };
    img.onerror = function () { clearTimeout(timer); if (token === self.token) self.fail(); };
    img.src = url;
  };

  /* Every page's box FIRST, at its true aspect ratio, then paint only what is
     near the viewport: the scroller gets its real height before any pixels
     arrive, so page 7 can be scrolled to before page 2 has painted. */
  Reader.prototype.layout = function () {
    var self = this;
    var scroll = this.els.scroll;
    /* Same token discipline as open(): closing mid-load rejects the in-flight
       getPage calls, and the catch below must not paint over the next document. */
    var token = this.token;

    scroll.replaceChildren();
    this.pages = [];
    this.setStatus('');

    var jobs = [];
    for (var i = 1; i <= this.doc.numPages; i++) jobs.push(this.doc.getPage(i));

    Promise.all(jobs).then(function (pages) {
      if (token !== self.token) return;
      pages.forEach(function (page, index) {
        var vp = page.getViewport({ scale: 1 });
        var box = document.createElement('div');
        box.className = 'pdocs-page';
        box.style.aspectRatio = vp.width + ' / ' + vp.height;
        box.dataset.page = String(index + 1);
        scroll.appendChild(box);
        self.pages.push({ page: page, box: box, task: null, scale: 0 });
      });

      /* Lay out BEFORE describing: current() reads box geometry, and unsized
         boxes made the counter enter the live region reading "10 / 10". */
      self.reflow();
      self.updateChrome();
      self.resizer.observe(scroll);
    })['catch'](function () {
      if (token !== self.token) return;
      self.fail();
    });
  };

  /* Width in CSS pixels one page gets at the current zoom: 1 is fit-width, above
     that the scroller pans. */
  Reader.prototype.pageWidth = function () {
    var scroll = this.els.scroll;
    /* Read the padding, not a constant: desktop is 1.6rem and mobile 0.8rem, so
       a fixed 32 left "fit width" 16px short on every phone. */
    var pad = getComputedStyle(scroll);
    var avail = scroll.clientWidth - (parseFloat(pad.paddingLeft) || 0) - (parseFloat(pad.paddingRight) || 0);
    /* Whole pixels, so a fingertip's worth of pinch does not queue a relayout
       per frame for a change nobody can see. */
    return Math.round(Math.max(160, avail * this.zoom));
  };

  /* LAY OUT at `width`, nothing else — cheap enough for every frame of a pinch,
     since the canvas is `width: 100%`. Idempotent by measurement, not a flag:
     that also swallows the callback ResizeObserver delivers on observe() and
     every height-only change (a phone collapsing its URL bar). Returns whether
     anything moved. */
  Reader.prototype.resize = function (width) {
    /* `!this.pages.length` is load-bearing: a zoom pressed while the document is
       still arriving recorded the width against zero boxes, and layout() was
       then refused the same number — a blank reader, no error (doc § Lifecycle). */
    if (!this.pages.length || !width || width === this.width) return false;
    this.width = width;
    for (var i = 0; i < this.pages.length; i++) this.pages[i].box.style.width = width + 'px';
    return true;
  };

  /* SHARPEN — the expensive half, every visible page re-rendered — kept out of
     resize() so a pinch lays out per frame and sharpens once, on lift. */
  Reader.prototype.repaint = function () {
    for (var i = 0; i < this.pages.length; i++) this.pages[i].scale = 0;
    /* A fresh observer re-delivers every page's current state, so painting keeps
       exactly one driver instead of a second path that walks the list itself. */
    this.watch();
  };

  /* Both, for the callers that are not a gesture: the resize observer and the
     first layout. */
  Reader.prototype.reflow = function () {
    if (this.resize(this.pages.length ? this.pageWidth() : 0)) this.repaint();
  };

  Reader.prototype.watch = function () {
    var self = this;
    if (this.observer) this.observer.disconnect();

    /* ONE job: paint what is coming into view, a screen early. Which page is
       current is current()'s job — a ratio cannot answer it. */
    this.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var item = self.pages[parseInt(entry.target.dataset.page, 10) - 1];
        if (!item) return;
        if (entry.isIntersecting) self.paint(item); else self.release(item);
      });
      self.updateCurrent();
    }, { root: this.els.scroll, rootMargin: '100% 0px' });

    this.pages.forEach(function (item) { self.observer.observe(item.box); });
  };

  /* A page more than a screen away gives its pixels back: MAX_CANVAS_PIXELS caps
     each canvas, this caps the total (doc § The reader). Emptying the box is
     safe ONLY because `.pdocs-viewer__scroll .pdocs-page` declares `display:
     block` at (0,2,0) over Dawn's `div:empty` (fact #9) — do not weaken it. */
  Reader.prototype.release = function (item) {
    if (!item.page) return; /* an <img>: the browser owns those pixels */
    if (item.task) { item.task.cancel(); item.task = null; }
    var canvas = item.box.firstElementChild;
    if (!canvas) return;
    canvas.width = 0;
    canvas.height = 0;
    item.box.replaceChildren();
    item.scale = 0;
  };

  Reader.prototype.paint = function (item) {
    if (!item.page) return; /* an <img> is already painted, at every zoom */
    var css = this.pageWidth();
    /* Render above CSS size for crispness, but never past the memory ceiling —
       and never repaint a page that is already sharp enough at this size. */
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var target = css * dpr;
    var base = item.page.getViewport({ scale: 1 });
    var ratio = base.width / base.height;
    if (target * (target / ratio) > MAX_CANVAS_PIXELS) target = Math.sqrt(MAX_CANVAS_PIXELS * ratio);
    if (item.scale && Math.abs(item.scale - target) < 2) return;

    if (item.task) { item.task.cancel(); item.task = null; }

    var viewport = item.page.getViewport({ scale: target / base.width });
    var canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.className = 'pdocs-page__canvas';

    item.scale = target;
    var task = item.page.render({ canvasContext: canvas.getContext('2d', { alpha: false }), viewport: viewport });
    item.task = task;
    task.promise.then(function () {
      if (item.task === task) item.task = null;
      /* Free the canvas this one replaces: a phone may not run a GC before the
         next zoom step. */
      var old = item.box.firstElementChild;
      if (old && old !== canvas) { old.width = 0; old.height = 0; }
      item.box.replaceChildren(canvas);
    })['catch'](function () {
      /* Only surrender the slot if THIS task still owns it: a cancelled render
         nulling a newer task rendered the page a third time (doc § Lifecycle). */
      if (item.task === task) { item.scale = 0; item.task = null; }
    });
  };

  /* ------------------------------------------------------------ navigation */

  /* WHICH PAGE IS THE CUSTOMER LOOKING AT — from geometry, NEVER from
     intersectionRatio, which is measured after rootMargin and reports 1.0 for
     every page shorter than the scroller (doc § Lifecycle). The midpoint cannot lie. */
  Reader.prototype.current = function () {
    var scroll = this.els.scroll;
    if (!scroll || !this.pages.length) return 0;
    var mid = scroll.getBoundingClientRect().top + scroll.clientHeight / 2;
    for (var i = 0; i < this.pages.length; i++) {
      if (this.pages[i].box.getBoundingClientRect().bottom > mid) return i;
    }
    return this.pages.length - 1;
  };

  /* "3 / 10". The slash is punctuation, not copy — keeping it out of the locale
     files means there is no format string to drift, and nothing to translate. */
  Reader.prototype.updateCurrent = function () {
    if (!this.pages.length) return;
    var next = (this.current() + 1) + ' / ' + this.pages.length;
    /* Write only on a real change: this is a polite live region and runs on every
       scroll tick — 68 writes for 5 changes, each one recited by a screen reader. */
    if (this.els.count.textContent !== next) this.els.count.textContent = next;
  };

  Reader.prototype.step = function (delta) {
    var next = this.current() + delta;
    if (next < 0 || next >= this.pages.length) return;
    var item = this.pages[next];
    this.paint(item);
    /* An explicit `behavior` OVERRIDES the CSS `scroll-behavior`, so reduced
       motion has to be read here, where the decision is made. */
    var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    item.box.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
  };

  /* Zoom so that whatever was under the fingers stays there — by ratios, since
     every page scales by the same factor but padding and gaps do not. `x`/`y`
     are client coordinates; none given anchors on the middle of the sheet, which
     is right for the buttons and the keyboard (doc § Gestures). */
  Reader.prototype.zoomTo = function (factor, x, y) {
    /* The contract: a factor is a positive number. NaN (a degenerate pinch) fails
       every comparison including the clamp and would poison every later zoom. */
    if (!(factor > 0)) return false;
    var next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, factor));
    if (next === this.zoom) return false;
    var scroll = this.els.scroll;
    var box = scroll.getBoundingClientRect();
    var px = (x === undefined ? box.left + scroll.clientWidth / 2 : x) - box.left;
    var py = (y === undefined ? box.top + scroll.clientHeight / 2 : y) - box.top;
    var fx = (scroll.scrollLeft + px) / scroll.scrollWidth;
    var fy = (scroll.scrollTop + py) / scroll.scrollHeight;

    this.zoom = next;
    this.updateChrome();
    if (!this.resize(this.pageWidth())) return false;

    scroll.scrollLeft = fx * scroll.scrollWidth - px;
    scroll.scrollTop = fy * scroll.scrollHeight - py;
    return true;
  };

  Reader.prototype.updateChrome = function () {
    var many = this.pages.length > 1;
    /* The page controls are not dimmed on a one-page document, they are absent:
       a disabled control still asks to be understood. */
    this.els.nav.hidden = !many;
    this.els.zoomIn.disabled = this.zoom >= MAX_ZOOM;
    this.els.zoomOut.disabled = this.zoom <= MIN_ZOOM;
    /* At high zoom the readout is the only proof a pinch did anything. Rounded —
       148.6% is noise. */
    this.els.scale.textContent = Math.round(this.zoom * 100) + '%';
    this.updateCurrent();
  };

  /* -------------------------------------------------------------- download */

  /* Named from the document's TITLE — the upload's filename is nobody's idea of
     a name — with the extension taken from the FILE: hardcoding `.pdf` saved a
     JPEG diagram as a corrupt PDF. */
  Reader.prototype.filename = function () {
    var ext = (this.url.split('?')[0].split('.').pop() || '').toLowerCase();
    if (!/^[a-z0-9]{1,5}$/.test(ext)) ext = 'pdf';
    var name = (this.title || 'document').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'document';
    return new RegExp('\\.' + ext + '$', 'i').test(name) ? name : name + '.' + ext;
  };

  /* The bytes are already here — pdf.js fetched them to render — so a blob
     costs no second request and lets the file arrive named after the title
     instead of the upload. The anchor's own `download` already works: these
     files are same-origin (doc § What the evidence refused). */
  Reader.prototype.save = function (event) {
    /* TAKE THE CLICK ONLY WHEN WE CAN BEAT THE ANCHOR. Taking it always made
       Download do nothing after a slow failure (doc § The reader, "Download is
       the ANCHOR"); the anchor's href cannot be blocked, double-fetch, or hang. */
    if (!this.doc || typeof this.doc.getData !== 'function') return;
    if (event) event.preventDefault();
    if (this.saving) return; /* a double click used to hand over the file twice */
    var self = this;
    this.saving = true;

    this.doc.getData().then(function (bytes) {
      var url = URL.createObjectURL(new Blob([bytes], { type: self.mime || 'application/octet-stream' }));
      var a = document.createElement('a');
      a.href = url;
      a.download = self.filename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
      self.saving = false;
    })['catch'](function () {
      /* Release the click back to the anchor rather than opening a popup the
         blocker will eat: the next press follows the real href natively. */
      self.saving = false;
    });
  };

  /* --------------------------------------------------------------- teardown */

  Reader.prototype.setStatus = function (text) {
    /* Unhide FIRST: `role="status"` is a live region only while in the
       accessibility tree, so text written into a hidden node is never announced. */
    this.els.status.hidden = !text;
    this.els.status.textContent = text;
  };

  /* The load is over, however it ended. Detaching the progress writer here —
     one place, both outcomes — is what stops it talking over the result. */
  Reader.prototype.done = function () {
    if (this.deadline) { clearTimeout(this.deadline); this.deadline = null; }
    if (this.loading) this.loading.onProgress = null;
  };

  Reader.prototype.fail = function () {
    this.done();
    this.setStatus(this.copy.failed);
    this.els.nav.hidden = true;
  };

  Reader.prototype.teardown = function () {
    this.token++;
    this.done();
    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    if (this.resizer) this.resizer.disconnect();
    var self = this;
    this.pages.forEach(function (item) { self.release(item); });
    this.pages = [];
    /* Forget the layout with its pages: a second document at the same viewport
       width matched `this.width` and its boxes were never sized (doc § Lifecycle). */
    this.width = 0;
    this.els.scroll.replaceChildren();
    if (this.loading) {
      try { this.loading.destroy(); } catch (e) { /* already gone */ }
      this.loading = null;
    }
    this.doc = null;
  };
  /* DEFINED LAST, and it is load-bearing: define() upgrades already-parsed
     elements SYNCHRONOUSLY, so connectedCallback runs right here, and declared
     earlier it reached `PARTS` still hoisted-undefined — a throw an upgrade
     swallows (doc § Lifecycle). */
  if (!customElements.get('product-documents')) {
    customElements.define('product-documents', ProductDocuments);
  }
})();
